import { Link } from '@tanstack/react-router';
import { CircleDollarSign, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { Skeleton } from '~/components/ui/skeleton';
import { Textarea } from '~/components/ui/textarea';
import { StatusLabel } from '~/features/cost-estimate/components/cost-estimate-card';
import { CostScenarioSummary } from '~/features/cost-estimate/components/cost-scenarios';
import { CostStatusChip } from '~/features/cost-estimate/components/cost-status-chip';
import { CostTotalValue } from '~/features/cost-estimate/components/cost-total-summary';
import { formatUsd } from '~/features/cost-estimate/utils/format-usd';
import { formatDateTime } from '~/utils/date';

import {
  useCatalogCanSeeCost,
  useCostDecision,
  useCostEstimateRetry,
  useRunCostEstimate,
} from '../hooks/use-run-ledger';
import { toCostEstimateView } from '../mappers/cost-gate.mapper';
import type { CostApprovalMode, CostGateWaiting } from '../types/run-ledger.api.types';
import { formatCount } from '../utils/run-format';

interface Props {
  runId: string;
  /** `progress.costGate` — present only while the gate holds the run before ENRICHMENT. */
  waiting: CostGateWaiting;
}

/**
 * §95 — the cost gate holding this run before ENRICHMENT: the estimate (the shared cost
 * components, fed catalog-service's estimate through `toCostEstimateView`) and the decision —
 * Approve (both providers), Approve Vertex only (when the estimate priced that scenario) or
 * Reject with a note. `canDecide` is the server's verdict for the caller (an APPROVERS-tier gate
 * is the approver list's; a SELF-tier one anyone in the subsidiary may decide).
 *
 * §96 — the gate fails closed: when no estimate could be made (`waiting.estimateError`) the run
 * is held all the same, says why, and offers Retry estimate to anyone (a retry never lets a run
 * on unpriced; the server also retries every 15 minutes).
 *
 * Customers see "Waiting for cost approval · being reviewed by Lily", the products and (when no
 * estimate could be made) Retry — no amounts, limits, scenarios, Approve / Reject or link to the
 * Cost approvals page. catalog-service still sends them the amounts: hiding is the web's alone.
 */
export function CostGatePanel({ runId, waiting }: Props) {
  const { t } = useTranslation('catalog-v2-run');
  const { t: tCommon } = useTranslation('common');
  const canSeeCost = useCatalogCanSeeCost();
  const estimateQuery = useRunCostEstimate(runId, true);
  const decision = useCostDecision(runId);
  const retry = useCostEstimateRetry(runId);
  const [notes, setNotes] = useState('');
  const unestimated = waiting.estimateError ?? null;
  const estimate = estimateQuery.data ? toCostEstimateView(estimateQuery.data) : null;

  const decide = (choice: 'APPROVE' | 'REJECT', mode?: CostApprovalMode) => {
    const trimmed = notes.trim();
    decision.mutate({
      decision: choice,
      ...(trimmed ? { notes: trimmed } : {}),
      ...(mode ? { mode } : {}),
    });
  };

  return (
    <section
      aria-label={t('costGate.title')}
      className="border-warning/40 bg-warning-50/40 flex flex-col gap-3 rounded-xl border p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-foreground flex items-center gap-1.5 text-base font-semibold">
          <CircleDollarSign aria-hidden="true" className="text-warning size-4" />
          {t('costGate.title')}
        </h2>
        <span className="flex items-center gap-3">
          {estimate ? (
            <CostStatusChip status={estimate.status}>
              <StatusLabel estimate={estimate} customer={!canSeeCost} />
            </CostStatusChip>
          ) : null}
          {canSeeCost ? (
            <Link
              to="/catalog-v2/cost-approvals"
              className="text-primary cursor-pointer text-xs font-medium hover:underline"
            >
              {t('costGate.allApprovals')}
            </Link>
          ) : null}
        </span>
      </div>
      <p className="text-muted-foreground text-sm" data-testid="cost-gate-body">
        {unestimated
          ? t('costGate.unestimatedBody')
          : !canSeeCost
            ? tCommon('costEstimate.customer.reviewing')
            : waiting.approvalTier === 'APPROVERS'
              ? t('costGate.approversBody')
              : t('costGate.selfBody')}{' '}
        {t('costGate.since', { at: formatDateTime(waiting.waitingSince) })}
      </p>
      {unestimated ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-muted-foreground text-xs break-all" data-testid="cost-gate-reason">
            {/* The raw reason can quote amounts: customers get a fixed line instead. */}
            {canSeeCost
              ? t('costGate.unestimatedReason', { reason: unestimated })
              : t('costGate.unestimatedReasonCustomer')}
          </p>
          <span className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">{t('costGate.retry.hint')}</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="cursor-pointer"
              isLoading={retry.isPending}
              disabled={retry.isPending || decision.isPending}
              onClick={() => {
                retry.mutate();
              }}
            >
              <RefreshCw aria-hidden="true" className="size-3.5" />
              {t('costGate.retry.action')}
            </Button>
          </span>
        </div>
      ) : null}

      {estimateQuery.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : !estimate ? (
        <p className="text-muted-foreground text-sm">{t('costGate.noEstimate')}</p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            {canSeeCost ? (
              <div>
                <dt className="text-muted-foreground text-xs">{t('costGate.total')}</dt>
                <dd className="text-foreground font-semibold">
                  <CostTotalValue total={estimate.total} />
                </dd>
              </div>
            ) : null}
            <div>
              <dt className="text-muted-foreground text-xs">{t('costGate.products')}</dt>
              <dd className="text-foreground font-semibold tabular-nums">
                {formatCount(estimate.productCount)}
              </dd>
            </div>
            {canSeeCost ? (
              <>
                <div>
                  <dt className="text-muted-foreground text-xs">{t('costGate.perProduct')}</dt>
                  <dd className="text-foreground font-semibold tabular-nums">
                    {estimate.perProduct ? formatUsd(estimate.perProduct.avgUsd) : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-xs">{t('costGate.threshold')}</dt>
                  <dd className="text-foreground font-semibold tabular-nums">
                    {estimate.approvalThresholdUsd !== null
                      ? formatUsd(estimate.approvalThresholdUsd)
                      : '—'}
                  </dd>
                </div>
              </>
            ) : null}
          </dl>
          {canSeeCost ? <CostScenarioSummary estimate={estimate} /> : null}

          {!canSeeCost ? null : estimate.canDecide ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor="cost-gate-notes" className="text-xs">
                {t('costGate.notes')}
              </Label>
              <Textarea
                id="cost-gate-notes"
                value={notes}
                onChange={(event) => {
                  setNotes(event.target.value);
                }}
                rows={2}
                maxLength={2000}
                placeholder={t('costGate.notesPlaceholder')}
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="cursor-pointer"
                  isLoading={decision.isPending && decision.variables.decision === 'APPROVE'}
                  disabled={decision.isPending}
                  onClick={() => {
                    decide('APPROVE', 'WITH_FALLBACK');
                  }}
                >
                  {t('costGate.approve')}
                </Button>
                {estimate.scenarios?.geminiOnly ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="cursor-pointer"
                    disabled={decision.isPending}
                    onClick={() => {
                      decide('APPROVE', 'VERTEX_ONLY');
                    }}
                  >
                    {t('costGate.approveVertexOnly')}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-error cursor-pointer"
                  isLoading={decision.isPending && decision.variables.decision === 'REJECT'}
                  disabled={decision.isPending}
                  onClick={() => {
                    decide('REJECT');
                  }}
                >
                  {t('costGate.reject')}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-xs">{t('costGate.cannotDecide')}</p>
          )}
        </>
      )}
    </section>
  );
}
